import { useEffect, useLayoutEffect, useState } from 'react';
import {
  apiGetProductCategories,
  apiGetProductCategoriesPublic,
  apiGetTextureCategories,
  apiGetTextureCategoriesPublic,
  apiUpdateProductCategories,
  apiUpdateTextureCategories,
} from '../../helpers/api';
import { checkIfMemberHaveEditorAccess, useUserContext } from '../../contexts/UserContext';
import { useDispatch } from 'react-redux';
import {
  setShowEditorNudge,
  setTriggerProductCategoryUpdate,
} from '../../redux/slicers/productV2Slicer';
import { getProductPageType, isTextureProductPage } from '../../helpers/jsHelper';

export const ALLOW_CATEGORY_UPLOAD = [
  getProductPageType().PRODUCTIMAGE,
  getProductPageType().DIMENSIONSHOT,
  getProductPageType().GIF,
  getProductPageType().VIDEO,
];

const useCategoryActions = ({
  productId,
  productInfo,
  category,
  isTexture,
  params = '',
  isPublicPage,
  updatePublicLink,
} = {}) => {
  const dispatch = useDispatch();
  const { currentUser, isFreemiumUser, isEditor, isGuestUser } = useUserContext();
  const currentOrganization = currentUser?.organization;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProductCategories = async () => {
    setLoading(true);
    const func = isTexture
      ? isPublicPage
        ? apiGetTextureCategoriesPublic
        : apiGetTextureCategories
      : isPublicPage
      ? apiGetProductCategoriesPublic
      : apiGetProductCategories;
    const response = await func({ id: productId, params });
    const _list = response?.results
      ?.map((cat) => {
        const type = cat?.[isTexture ? 'texturetype' : 'producttype'];
        const is3dOrArViewer =
          type === getProductPageType().THREEDVIEWER || type === getProductPageType().ARVIEWER;
        return {
          ...cat,
          __no_upload: !ALLOW_CATEGORY_UPLOAD.includes(
            cat?.[isTexture ? 'texturetype' : 'producttype']
          ), // TODO: Check with the team about to hide the upload for the Freemium user only or Should we hide the upload for the Old Subscription & Old Premium user.
          __no_edit_configurator:
            isPublicPage ||
            !isGuestUser() ||
            !isEditor ||
            type !== getProductPageType().CONFIGURATOR,
          hide: isPublicPage
            ? is3dOrArViewer
              ? !is3dArViewerAvailable()
              : 'total_count' in cat
              ? cat.total_count === 0
              : false
            : false,
        };
      })
      .filter((ins) => !ins.hide);
    if (_list && setList) setList?.(_list || []);
    setLoading(false);
    return _list;
  };

  useLayoutEffect(() => {
    if (!productId) return;
    fetchProductCategories();
  }, [productId, currentOrganization]);

  /**
   * Check if data for AR viewer or 3D viewer is available or not
   */
  const is3dArViewerAvailable = () => {
    const hasContent = Boolean(productInfo?.has_content);
    const glb_file =
      productInfo?.glb_file || productInfo?.texture_page_product_details?.glb_bundle_file;
    const unicode = productInfo?.unicode || productInfo?.texture_page_product_details?.unicode;
    if (
      !isTexture ? productInfo?.generate_ar_content_status === 1 : hasContent && glb_file && unicode
    ) {
      return true;
    }
    return false;
  };

  /**
   * Updates the order of product categories for a given product.
   *
   * @param {Object} [options] - The options object.
   * @param {Array} [options.data=[]] - The updated list of product categories with their new sequence numbers.
   * @param {boolean} [options.fetchData=true] - Whether to fetch the updated product categories after the update.
   * @returns {Promise<void>} - A Promise that resolves when the categories have been updated.
   */
  const updateCategoriesOrder = async ({ data = [], fetchData = true } = {}) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(isGuestUser() ? true : false)) {
      !isGuestUser() && dispatch(setShowEditorNudge(true));
      return;
    }
    if (!productId || data?.length === 0) return;
    const func = isTexture ? apiUpdateTextureCategories : apiUpdateProductCategories;
    setList(data);
    const response = await func({
      id: productId,
      data: JSON.stringify(
        data.map((item) => ({
          id: item.id,
          seq_no: item.seq_no,
        }))
      ),
    });
    dispatch(setTriggerProductCategoryUpdate(true));
    if (response) {
      updatePublicLink?.();
      if (fetchData) fetchProductCategories();
    } else fetchProductCategories();
  };

  /**
   * Moves a product category to the top of the list and updates the order of the remaining categories.
   *
   * @param {React.MouseEvent<HTMLElement>} e - The click event object.
   * @param {string} id - The ID of the product category to move to the top.
   */
  const onMoveToTop = (e, id) => {
    if (!list || list?.length === 0 || !id) return;
    e?.preventDefault();
    e?.stopPropagation();
    const row = list?.find((item) => item.id === id);
    const newList = list?.filter((item) => item.id !== id);
    const _list = [
      {
        ...row,
        seq_no: 1,
      },
      ...newList.map((item, i) => ({
        ...item,
        seq_no: i + 2,
      })),
    ];
    // setList(_list);
    updateCategoriesOrder({
      data: _list,
      fetchData: false,
    });
  };

  return {
    list,
    setList,
    fetchProductCategories,
    updateCategoriesOrder,
    onMoveToTop,
    loading,
  };
};

export default useCategoryActions;
